import { useEffect, useState, useMemo } from "react";
import DashboardShell from "../components/shell/DashboardShell";
import api, { formatApiError } from "../lib/api";
import { toast } from "sonner";
import { Inbox, Mail, Phone, Building2, Clock, CheckCircle2, MessageSquare, Megaphone, MapPin, Ruler, Trash2 } from "lucide-react";
import { TYPE_BY_VALUE } from "../lib/propertyTypes";
import { formatUSPhone, telHref } from "../lib/phone";

const STATUS_STYLES = {
  new:       { bg: "bg-blue-100",    fg: "text-blue-800",    Icon: Inbox,        label: "New" },
  contacted: { bg: "bg-amber-100",   fg: "text-amber-800",   Icon: Clock,        label: "Contacted" },
  closed:    { bg: "bg-emerald-100", fg: "text-emerald-800", Icon: CheckCircle2, label: "Closed" },
};

const SOURCE_STYLES = {
  linkedin: { emoji: "in", bg: "bg-sky-50 border-sky-200 text-sky-800" },
  facebook: { emoji: "fb", bg: "bg-indigo-50 border-indigo-200 text-indigo-800" },
  twitter:  { emoji: "𝕏",  bg: "bg-slate-900 border-slate-900 text-white" },
  direct:   { emoji: "🔗", bg: "bg-slate-50 border-slate-200 text-slate-700" },
};

export default function Leads() {
  const [tab, setTab] = useState("inquiries"); // "inquiries" | "space_requests"

  // --- Inquiries state ---
  const [items, setItems] = useState([]);
  const [byStatus, setByStatus] = useState({ new: 0, contacted: 0, closed: 0 });
  const [byUtm, setByUtm] = useState({});
  const [filter, setFilter] = useState("");
  const [statusFilter, setStatusFilter] = useState("");
  const [selectedIds, setSelectedIds] = useState(() => new Set());

  // --- Space requests state ---
  const [spaceItems, setSpaceItems] = useState([]);
  const [spaceFilter, setSpaceFilter] = useState("");

  const load = async () => {
    try {
      const { data } = await api.get("/leads");
      setItems(data.items || []);
      setByStatus(data.by_status || { new: 0, contacted: 0, closed: 0 });
      setByUtm(data.by_utm_source || {});
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Load failed");
    }
  };

  const loadSpace = async () => {
    try {
      const { data } = await api.get("/space-requests");
      setSpaceItems(data.items || []);
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Load failed");
    }
  };

  useEffect(() => { load(); loadSpace(); }, []);

  const setStatus = async (lid, status) => {
    try {
      await api.put(`/leads/${lid}/status`, null, { params: { status } });
      toast.success(`Marked ${status}`);
      load();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Update failed");
    }
  };

  const deleteLead = async (lead) => {
    if (!confirm(`Delete the lead from "${lead.name || lead.email}"? This cannot be undone.`)) return;
    try {
      await api.delete(`/leads/${lead.id}`);
      toast.success("Lead deleted");
      setSelectedIds((prev) => {
        const next = new Set(prev);
        next.delete(lead.id);
        return next;
      });
      load();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Delete failed");
    }
  };

  const toggleSelected = (id) => {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const toggleSelectAll = (visibleIds) => {
    setSelectedIds((prev) => {
      const allSelected = visibleIds.length > 0 && visibleIds.every((id) => prev.has(id));
      if (allSelected) {
        const next = new Set(prev);
        visibleIds.forEach((id) => next.delete(id));
        return next;
      }
      const next = new Set(prev);
      visibleIds.forEach((id) => next.add(id));
      return next;
    });
  };

  const bulkDelete = async () => {
    const ids = Array.from(selectedIds);
    if (ids.length === 0) return;
    if (!confirm(`Permanently delete ${ids.length} lead${ids.length === 1 ? "" : "s"}? This cannot be undone.`)) return;
    try {
      const r = await api.post("/leads/bulk-delete", { ids });
      toast.success(`Deleted ${r.data?.deleted ?? ids.length} lead${(r.data?.deleted ?? ids.length) === 1 ? "" : "s"}`);
      setSelectedIds(new Set());
      load();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Bulk delete failed");
    }
  };

  const filtered = useMemo(() => {
    return items.filter((l) => {
      if (statusFilter && (l.status || "new") !== statusFilter) return false;
      if (!filter.trim()) return true;
      const q = filter.trim().toLowerCase();
      return (
        (l.name || "").toLowerCase().includes(q) ||
        (l.email || "").toLowerCase().includes(q) ||
        (l.property_name || "").toLowerCase().includes(q) ||
        (l.message || "").toLowerCase().includes(q)
      );
    });
  }, [items, filter, statusFilter]);

  const filteredSpace = useMemo(() => {
    if (!spaceFilter.trim()) return spaceItems;
    const q = spaceFilter.trim().toLowerCase();
    return spaceItems.filter((s) =>
      (s.name || "").toLowerCase().includes(q) ||
      (s.email || "").toLowerCase().includes(q) ||
      (s.address || "").toLowerCase().includes(q) ||
      (s.message || "").toLowerCase().includes(q)
    );
  }, [spaceItems, spaceFilter]);

  return (
    <DashboardShell>
      <div className="p-4 sm:p-6 lg:p-8">
        <div className="flex items-start justify-between mb-6 gap-3 flex-wrap">
          <div>
            <div className="overline text-slate-500">Pipeline</div>
            <h1 className="text-3xl font-bold tracking-tight text-slate-900" style={{ fontFamily: "Outfit" }} data-testid="leads-heading">
              Prospects & space requests
            </h1>
            <p className="text-sm text-slate-500 mt-1">
              {tab === "inquiries"
                ? `${byStatus.new} new · ${byStatus.contacted} contacted · ${byStatus.closed} closed`
                : `${spaceItems.length} broadcast${spaceItems.length === 1 ? "" : "s"} matching your portfolio`}
            </p>
          </div>
        </div>

        {/* Tabs */}
        <div className="flex items-center gap-1 mb-6 border-b border-slate-200" data-testid="leads-tabs">
          <button
            onClick={() => setTab("inquiries")}
            data-testid="leads-tab-inquiries"
            className={`px-4 py-2.5 text-sm font-mono uppercase tracking-widest -mb-px border-b-2 transition-colors ${
              tab === "inquiries"
                ? "border-slate-900 text-slate-900"
                : "border-transparent text-slate-500 hover:text-slate-900"
            }`}
          >
            <Inbox className="w-3.5 h-3.5 inline mr-1.5" />
            Inquiries ({items.length})
          </button>
          <button
            onClick={() => setTab("space_requests")}
            data-testid="leads-tab-space-requests"
            className={`px-4 py-2.5 text-sm font-mono uppercase tracking-widest -mb-px border-b-2 transition-colors ${
              tab === "space_requests"
                ? "border-slate-900 text-slate-900"
                : "border-transparent text-slate-500 hover:text-slate-900"
            }`}
          >
            <Megaphone className="w-3.5 h-3.5 inline mr-1.5" />
            Space requests ({spaceItems.length})
          </button>
        </div>

        {tab === "inquiries" ? (
          <>
            <div className="flex items-center gap-2 mb-4 flex-wrap">
              <input
                type="search"
                placeholder="Search leads…"
                className="input-clean max-w-xs"
                data-testid="leads-search-input"
                value={filter}
                onChange={(e) => setFilter(e.target.value)}
              />
              <select
                className="input-clean"
                data-testid="leads-status-filter"
                value={statusFilter}
                onChange={(e) => setStatusFilter(e.target.value)}
              >
                <option value="">All statuses ({items.length})</option>
                <option value="new">New ({byStatus.new})</option>
                <option value="contacted">Contacted ({byStatus.contacted})</option>
                <option value="closed">Closed ({byStatus.closed})</option>
              </select>
              {filtered.length > 0 ? (
                <label className="inline-flex items-center gap-1.5 text-xs font-mono uppercase tracking-widest text-slate-600 ml-1 cursor-pointer">
                  <input
                    type="checkbox"
                    className="w-3.5 h-3.5 cursor-pointer"
                    checked={filtered.length > 0 && filtered.every((l) => selectedIds.has(l.id))}
                    onChange={() => toggleSelectAll(filtered.map((l) => l.id))}
                    data-testid="leads-select-all"
                    aria-label="Select all visible leads"
                  />
                  Select all ({filtered.length})
                </label>
              ) : null}
              {selectedIds.size > 0 ? (
                <div className="ml-auto flex items-center gap-2 bg-rose-50 border border-rose-200 rounded-sm px-3 py-1.5" data-testid="leads-bulk-bar">
                  <span className="text-xs font-mono uppercase tracking-widest text-rose-800">
                    {selectedIds.size} selected
                  </span>
                  <button
                    type="button"
                    onClick={bulkDelete}
                    className="text-xs font-mono uppercase tracking-widest px-3 py-1 bg-rose-700 text-white hover:bg-rose-800 rounded-sm inline-flex items-center gap-1"
                    data-testid="leads-bulk-delete"
                  >
                    <Trash2 className="w-3 h-3" /> Delete selected
                  </button>
                  <button
                    type="button"
                    onClick={() => setSelectedIds(new Set())}
                    className="text-xs font-mono uppercase tracking-widest px-2 py-1 text-rose-700 hover:bg-rose-100 rounded-sm"
                    data-testid="leads-bulk-clear"
                  >
                    Clear
                  </button>
                </div>
              ) : null}
            </div>

            {/* Attribution strip — where are your leads coming from? */}
            {items.length > 0 && (
              <div className="mb-4 bg-white border border-slate-200 rounded-sm px-4 py-3 flex items-center gap-2 flex-wrap" data-testid="leads-attribution-strip">
                <span className="overline text-slate-500 mr-2">By source</span>
                {Object.entries(byUtm)
                  .sort((a, b) => b[1] - a[1])
                  .map(([src, count]) => (
                    <span
                      key={src}
                      className={`inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-[11px] font-mono uppercase tracking-widest border ${SOURCE_STYLES[src]?.bg || "bg-slate-50 border-slate-200 text-slate-700"}`}
                      data-testid={`leads-utm-${src}`}
                    >
                      {SOURCE_STYLES[src]?.emoji || "🔗"} {src} <span className="opacity-60">({count})</span>
                    </span>
                  ))}
              </div>
            )}

            {filtered.length === 0 ? (
              <div className="bg-white border border-dashed border-slate-200 rounded-sm p-16 text-center">
                <Inbox className="w-10 h-10 mx-auto text-slate-300 mb-3" />
                <div className="overline text-slate-500">No leads yet</div>
                <p className="text-sm text-slate-500 mt-2 max-w-md mx-auto">
                  Inquiries sent from the "Properties near me" widget on your landing page will appear here instantly.
                </p>
              </div>
            ) : (
              <div className="grid gap-3" data-testid="leads-list">
                {filtered.map((lead) => {
                  const s = STATUS_STYLES[lead.status || "new"];
                  const S = s.Icon;
                  const dt = lead.created_at ? new Date(lead.created_at).toLocaleString() : "";
                  return (
                    <div key={lead.id} className={`bg-white border rounded-sm p-5 transition-colors ${selectedIds.has(lead.id) ? "border-rose-300 ring-1 ring-rose-200" : "border-slate-200"}`} data-testid={`lead-row-${lead.id}`}>
                      <div className="flex items-start justify-between gap-3 flex-wrap">
                        <div className="flex items-start gap-3 min-w-0 flex-1">
                          <input
                            type="checkbox"
                            className="w-3.5 h-3.5 mt-1.5 cursor-pointer shrink-0"
                            checked={selectedIds.has(lead.id)}
                            onChange={() => toggleSelected(lead.id)}
                            data-testid={`lead-select-${lead.id}`}
                            aria-label={`Select lead from ${lead.name || lead.email}`}
                          />
                          <div className="min-w-0">
                            <div className="flex items-center gap-2 flex-wrap">
                            <span className="font-bold text-slate-900" style={{ fontFamily: "Outfit" }}>{lead.name}</span>
                            <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-sm text-[10px] font-mono uppercase tracking-widest ${s.bg} ${s.fg}`}>
                              <S className="w-3 h-3" /> {s.label}
                            </span>
                            {lead.utm_source && (
                              <span
                                className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-sm text-[10px] font-mono uppercase tracking-widest border ${SOURCE_STYLES[lead.utm_source]?.bg || "bg-slate-50 border-slate-200 text-slate-700"}`}
                                title={`Attribution: ${lead.utm_source}`}
                                data-testid={`lead-utm-${lead.id}`}
                              >
                                {SOURCE_STYLES[lead.utm_source]?.emoji || "🔗"} via {lead.utm_source}
                              </span>
                            )}
                            <span className="text-[10px] font-mono uppercase tracking-widest text-slate-400">{dt}</span>
                          </div>
                          <div className="mt-2 flex flex-wrap gap-x-4 gap-y-1 text-xs font-mono text-slate-600">
                            <a href={`mailto:${lead.email}`} className="flex items-center gap-1 hover:text-slate-900"><Mail className="w-3 h-3" /> {lead.email}</a>
                            {lead.phone && (
                              <a href={telHref(lead.phone) || `tel:${lead.phone}`} className="flex items-center gap-1 hover:text-slate-900" data-testid={`lead-phone-link-${lead.id}`} title="Click to dial"><Phone className="w-3 h-3" /> {formatUSPhone(lead.phone)}</a>
                            )}
                            {lead.property_name && (
                              <span className="flex items-center gap-1"><Building2 className="w-3 h-3" /> {lead.property_name}</span>
                            )}
                          </div>
                          {lead.message && (
                            <div className="mt-3 flex items-start gap-2 text-sm text-slate-700 bg-slate-50 border border-slate-100 rounded-sm p-3">
                              <MessageSquare className="w-4 h-4 text-slate-400 mt-0.5 shrink-0" />
                              <span className="whitespace-pre-wrap">{lead.message}</span>
                            </div>
                          )}
                          </div>
                        </div>
                        <div className="flex items-center gap-1 shrink-0">
                          {lead.status !== "contacted" && (
                            <button
                              onClick={() => setStatus(lead.id, "contacted")}
                              className="text-xs font-mono uppercase tracking-widest px-3 py-1.5 border border-slate-200 rounded-sm hover:bg-slate-50"
                              data-testid={`lead-mark-contacted-${lead.id}`}
                            >Mark contacted</button>
                          )}
                          {lead.status !== "closed" && (
                            <button
                              onClick={() => setStatus(lead.id, "closed")}
                              className="text-xs font-mono uppercase tracking-widest px-3 py-1.5 border border-slate-200 rounded-sm hover:bg-slate-50"
                              data-testid={`lead-mark-closed-${lead.id}`}
                            >Close</button>
                          )}
                          <button
                            onClick={() => deleteLead(lead)}
                            className="text-rose-500 hover:text-rose-700 hover:bg-rose-50 p-1.5 rounded-sm"
                            title="Delete lead"
                            data-testid={`lead-delete-${lead.id}`}
                          >
                            <Trash2 className="w-3.5 h-3.5" />
                          </button>
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </>
        ) : (
          <>
            <div className="flex items-center gap-2 mb-4 flex-wrap">
              <input
                type="search"
                placeholder="Search space requests…"
                className="input-clean max-w-xs"
                data-testid="space-requests-search-input"
                value={spaceFilter}
                onChange={(e) => setSpaceFilter(e.target.value)}
              />
            </div>

            {filteredSpace.length === 0 ? (
              <div className="bg-white border border-dashed border-slate-200 rounded-sm p-16 text-center" data-testid="space-requests-empty">
                <Megaphone className="w-10 h-10 mx-auto text-slate-300 mb-3" />
                <div className="overline text-slate-500">No space requests yet</div>
                <p className="text-sm text-slate-500 mt-2 max-w-md mx-auto">
                  Tenants looking for space can broadcast their requirements from the public{" "}
                  <a href="/find-space" className="underline hover:text-slate-900" target="_blank" rel="noreferrer">Find Space</a> page.
                  You'll see the ones matching your portfolio here.
                </p>
              </div>
            ) : (
              <div className="grid gap-3" data-testid="space-requests-list">
                {filteredSpace.map((sr) => {
                  const dt = sr.created_at ? new Date(sr.created_at).toLocaleString() : "";
                  const types = (sr.property_types || []).map((t) => TYPE_BY_VALUE[t]?.label || t);
                  return (
                    <div key={sr.id} className="bg-white border border-slate-200 rounded-sm p-5" data-testid={`space-request-row-${sr.id}`}>
                      <div className="flex items-start justify-between gap-3 flex-wrap">
                        <div className="min-w-0 flex-1">
                          <div className="flex items-center gap-2 flex-wrap">
                            <span className="font-bold text-slate-900" style={{ fontFamily: "Outfit" }}>{sr.name}</span>
                            <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-sm text-[10px] font-mono uppercase tracking-widest bg-violet-100 text-violet-800">
                              <Megaphone className="w-3 h-3" /> Tenant broadcast
                            </span>
                            <span className="text-[10px] font-mono uppercase tracking-widest text-slate-400">{dt}</span>
                          </div>
                          <div className="mt-2 flex flex-wrap gap-x-4 gap-y-1 text-xs font-mono text-slate-600">
                            <a href={`mailto:${sr.email}`} className="flex items-center gap-1 hover:text-slate-900"><Mail className="w-3 h-3" /> {sr.email}</a>
                            {sr.phone && (
                              <a href={telHref(sr.phone) || `tel:${sr.phone}`} className="flex items-center gap-1 hover:text-slate-900" data-testid={`space-request-phone-link-${sr.id}`} title="Click to dial"><Phone className="w-3 h-3" /> {formatUSPhone(sr.phone)}</a>
                            )}
                            {sr.address && (
                              <span className="flex items-center gap-1"><MapPin className="w-3 h-3" /> {sr.address}</span>
                            )}
                            {sr.radius_miles != null && (
                              <span className="flex items-center gap-1"><Ruler className="w-3 h-3" /> {sr.radius_miles} mi radius</span>
                            )}
                          </div>
                          {(types.length > 0 || sr.min_units) && (
                            <div className="mt-2 flex flex-wrap gap-1.5">
                              {types.map((t) => (
                                <span key={t} className="text-[10px] font-mono uppercase tracking-widest px-2 py-0.5 rounded-full bg-slate-100 text-slate-700 border border-slate-200">
                                  {t}
                                </span>
                              ))}
                              {sr.min_units ? (
                                <span className="text-[10px] font-mono uppercase tracking-widest px-2 py-0.5 rounded-full bg-slate-100 text-slate-700 border border-slate-200">
                                  {sr.min_units}+ units
                                </span>
                              ) : null}
                            </div>
                          )}
                          {sr.message && (
                            <div className="mt-3 flex items-start gap-2 text-sm text-slate-700 bg-slate-50 border border-slate-100 rounded-sm p-3">
                              <MessageSquare className="w-4 h-4 text-slate-400 mt-0.5 shrink-0" />
                              <span className="whitespace-pre-wrap">{sr.message}</span>
                            </div>
                          )}
                          {sr.matching_property_names?.length > 0 && (
                            <div className="mt-3 text-xs text-slate-500">
                              <span className="overline text-slate-400 mr-2">Your matches</span>
                              {sr.matching_property_names.slice(0, 4).map((n, i) => (
                                <span key={`${sr.id}-${n}-${i}`} className="inline-flex items-center gap-1 mr-2 text-slate-700">
                                  <Building2 className="w-3 h-3" /> {n}
                                </span>
                              ))}
                              {sr.matching_property_names.length > 4 && (
                                <span className="text-slate-400">+{sr.matching_property_names.length - 4} more</span>
                              )}
                            </div>
                          )}
                        </div>
                        <div className="flex items-center gap-1 shrink-0">
                          <a
                            href={`mailto:${sr.email}?subject=${encodeURIComponent("Re: your space request")}`}
                            className="text-xs font-mono uppercase tracking-widest px-3 py-1.5 border border-slate-200 rounded-sm hover:bg-slate-50"
                            data-testid={`space-request-reply-${sr.id}`}
                          >
                            Reply
                          </a>
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </>
        )}
      </div>
    </DashboardShell>
  );
}
